import * as React from 'react';

import { useSession } from '@/components/auth/user-provider';

export type WorkspaceMode = 'all' | 'b2b' | 'b2c' | 'partner' | 'investor';

export const WORKSPACE_MODES: Record<WorkspaceMode, {
  label: string;
  shortLabel: string;
  description: string;
  relationshipType?: 'b2b_customer' | 'b2c_consumer' | 'partner' | 'investor';
  accountKind?: 'organization' | 'person';
  pipeline?: 'b2b_sales' | 'b2c_sales' | 'investor_funding';
}> = {
  all: { label: 'All views', shortLabel: 'All', description: 'All menus and data across every relationship' },
  b2c: { label: 'Consumers view', shortLabel: 'Consumers', description: 'Consumers, consent and purchase journeys', relationshipType: 'b2c_consumer', accountKind: 'person', pipeline: 'b2c_sales' },
  b2b: { label: 'Business view', shortLabel: 'Business', description: 'Business customers and consultative sales', relationshipType: 'b2b_customer', accountKind: 'organization', pipeline: 'b2b_sales' },
  partner: { label: 'Partner view', shortLabel: 'Partners', description: 'Channel relationships and joint opportunities', relationshipType: 'partner', accountKind: 'organization', pipeline: 'b2b_sales' },
  investor: { label: 'Investor view', shortLabel: 'Investors', description: 'Investor relations and funding rounds', relationshipType: 'investor', accountKind: 'organization', pipeline: 'investor_funding' }
};

type WorkspaceContextValue = {
  mode: WorkspaceMode;
  setMode: (mode: WorkspaceMode) => void;
  config: (typeof WORKSPACE_MODES)[WorkspaceMode];
};

const WorkspaceContext = React.createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { session } = useSession();
  const userKey = session.status === 'signedIn' ? session.user.email || session.user.name : 'anonymous';
  const storageKey = `cdf-workspace-mode:${userKey.toLowerCase()}`;
  const [mode, setModeState] = React.useState<WorkspaceMode>('b2b');

  React.useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    setModeState(saved && saved in WORKSPACE_MODES ? saved as WorkspaceMode : 'b2b');
  }, [storageKey]);

  const setMode = React.useCallback((next: WorkspaceMode) => {
    setModeState(next);
    localStorage.setItem(storageKey, next);
  }, [storageKey]);

  React.useEffect(() => {
    document.documentElement.dataset.workspace = mode;
    return () => { delete document.documentElement.dataset.workspace; };
  }, [mode]);

  const value = React.useMemo(() => ({ mode, setMode, config: WORKSPACE_MODES[mode] }), [mode, setMode]);
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

/** Workspace context when available (null in isolated shells and tests without the provider). */
export function useOptionalWorkspace() {
  return React.useContext(WorkspaceContext);
}

export function useWorkspace() {
  const context = React.useContext(WorkspaceContext);
  if (!context) throw new Error('useWorkspace must be used inside WorkspaceProvider');
  return context;
}
